import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import LandingPage from './components/LandingPage';
import AuthModal from './components/AuthModal';
import Dashboard from './components/Dashboard';
import MobileOptimizedHeader from './components/MobileOptimizedHeader';
import MobileOptimizedTabNavigation from './components/MobileOptimizedTabNavigation';
import ProjectInformation from './components/ProjectInformation';
import OwnerProjectRequirements from './components/OwnerProjectRequirements';
import SyncResults from './components/SyncResults';
import './App.css';

// PWA Installation prompt
const InstallPWA = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showInstallButton, setShowInstallButton] = useState(false);

  useEffect(() => {
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShowInstallButton(true);
    };

    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;
    
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    
    if (outcome === 'accepted') {
      setDeferredPrompt(null);
      setShowInstallButton(false);
    }
  };

  if (!showInstallButton) return null;

  return (
    <motion.div
      initial={{ y: 100, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      className="fixed bottom-4 left-4 right-4 z-50 md:left-auto md:right-4 md:w-80"
    >
      <div className="bg-primary text-white p-4 rounded-2xl shadow-2xl">
        <div className="flex items-center justify-between">
          <div className="flex-1">
            <p className="font-semibold text-sm">Install BQ SyncSpec</p>
            <p className="text-xs opacity-90">Add to home screen for easy access</p>
          </div>
          <button
            onClick={handleInstall}
            className="ml-3 px-3 py-1 bg-white text-primary rounded-lg text-sm font-medium"
          >
            Install
          </button>
        </div>
      </div>
    </motion.div>
  );
};

const AppContent = () => {
  const { isAuthenticated, loading } = useAuth();
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [currentView, setCurrentView] = useState('dashboard'); // 'dashboard' or 'project'
  const [currentStep, setCurrentStep] = useState(1);
  const [partOneCompleted, setPartOneCompleted] = useState(false);
  const [projectData, setProjectData] = useState(() => {
    const savedData = localStorage.getItem('projectData');
    return savedData ? JSON.parse(savedData) : {
      projectName: '',
      owner: '',
      leadProfession: '',
      leadName: '',
      occupancy: '',
      startDate: '',
      location: '',
      contact: '',
      email: '',
      mobile: '',
      selectedSAC: '',
      spaceType: '',
      roomType: '',
      areaSqm: '',
      lengthM: '',
      widthM: '',
      ceilingType: '',
      accentConfig: '',
      accentSubtype: '',
      materialType: '',
      materialSpecs: {
        type: '',
        thickness: '',
        brand: '',
        paintBrand: '',
        paintType: '',
        paintTexture: ''
      },
      specialRequirements: ''
    };
  });

  // Register service worker for PWA
  useEffect(() => {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.register('/sw.js')
        .then((registration) => {
          console.log('SW registered: ', registration);
        })
        .catch((registrationError) => {
          console.log('SW registration failed: ', registrationError);
        });
    }
  }, []);

  // Save project data to localStorage
  useEffect(() => {
    localStorage.setItem('projectData', JSON.stringify(projectData));
  }, [projectData]);

  // Check if part one is completed
  useEffect(() => {
    const requiredFields = [
      'projectName', 'owner', 'leadProfession', 'leadName', 
      'occupancy', 'startDate', 'location', 'contact', 'email', 'mobile'
    ];
    const isCompleted = requiredFields.every(field => !!projectData[field]);
    setPartOneCompleted(isCompleted);
  }, [projectData]);

  const updateProjectData = (updates) => {
    setProjectData(prev => {
      if (updates.materialSpecs) {
        return {
          ...prev,
          ...updates,
          materialSpecs: {
            ...prev.materialSpecs,
            ...updates.materialSpecs
          }
        };
      }
      return { ...prev, ...updates };
    });
  };

  const handleCreateProject = () => {
    setCurrentView('project');
    setCurrentStep(1);
  };

  const handleOpenProject = (project) => {
    // Load project data
    setCurrentView('project');
    setCurrentStep(1);
  };

  const handleBackToDashboard = () => {
    setCurrentView('dashboard');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 flex items-center justify-center">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
          className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full"
        />
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <>
        <LandingPage onGetStarted={() => setShowAuthModal(true)} />
        <AuthModal 
          isOpen={showAuthModal} 
          onClose={() => setShowAuthModal(false)} 
        />
        <InstallPWA />
      </>
    );
  }

  if (currentView === 'dashboard') {
    return (
      <>
        <Dashboard 
          onCreateProject={handleCreateProject}
          onOpenProject={handleOpenProject}
        />
        <InstallPWA />
      </>
    );
  }

  // Project view with mobile-optimized interface
  return (
    <div className="min-h-screen bg-gray-50">
      <MobileOptimizedHeader 
        title="BQ SyncSpec" 
        subtitle="C3030 Ceiling Assemblies"
      />
      
      <MobileOptimizedTabNavigation 
        currentStep={currentStep}
        setCurrentStep={setCurrentStep}
        partOneCompleted={partOneCompleted}
      />

      <main className="pb-20">
        <AnimatePresence mode="wait">
          {currentStep === 1 && (
            <motion.div
              key="step1"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
              className="p-4"
            >
              <ProjectInformation 
                data={projectData}
                updateData={updateProjectData}
                onNext={() => setCurrentStep(2)}
              />
            </motion.div>
          )}

          {currentStep === 2 && (
            <motion.div
              key="step2"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
              className="p-4"
            >
              <OwnerProjectRequirements 
                data={projectData}
                updateData={updateProjectData}
                onNext={() => setCurrentStep(3)}
                onBack={() => setCurrentStep(1)}
                partOneCompleted={partOneCompleted}
              />
            </motion.div>
          )}

          {currentStep === 3 && (
            <motion.div
              key="step3"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
              className="p-4"
            >
              <SyncResults 
                data={projectData}
                onBack={() => setCurrentStep(2)}
                partOneCompleted={partOneCompleted}
              />
            </motion.div>
          )}
        </AnimatePresence>
      </main>

      {/* Mobile bottom navigation */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-4 safe-area-pb">
        <div className="flex justify-between gap-4">
          <motion.button
            whileTap={{ scale: 0.95 }}
            onClick={handleBackToDashboard}
            className="px-4 py-2 text-gray-600 hover:text-gray-900 font-medium rounded-xl transition-colors"
          >
            ← Dashboard
          </motion.button>
          
          <div className="flex gap-2">
            {currentStep > 1 && (
              <motion.button
                whileTap={{ scale: 0.95 }}
                onClick={() => setCurrentStep(currentStep - 1)}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-xl font-medium hover:bg-gray-200 transition-colors"
              >
                Back
              </motion.button>
            )}
            
            {currentStep < 3 && (
              <motion.button
                whileTap={{ scale: 0.95 }}
                onClick={() => setCurrentStep(currentStep + 1)}
                className={`px-6 py-2 rounded-xl font-medium transition-colors ${
                  (currentStep === 1 && !partOneCompleted) 
                    ? 'bg-gray-200 text-gray-500' 
                    : 'bg-primary text-white hover:bg-primary-dark'
                }`}
                disabled={currentStep === 1 && !partOneCompleted}
              >
                {currentStep === 1 ? 'Continue' : 'Generate'}
              </motion.button>
            )}
          </div>
        </div>
      </div>

      <InstallPWA />
    </div>
  );
};

function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}

export default App;